import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormControl } from '@ngneat/reactive-forms';
import { saveAs } from 'file-saver';
import { ImageModalComponent } from 'src/app/generic/components/image-modal/image-modal.component';
import { PlanningService } from 'src/app/services/planning/planning.service';
import { UserService } from 'src/app/services/user/user.service';
import { LocalStorage } from 'src/app/storage.class';
import { environment } from 'src/environments/environment';
import { PlanningFile } from '../../../../classes/planning-file';
import { User } from '../../../../classes/user.class';
import { CodaltComponent } from '../../../../codalt.component';
import { CodaltUploadFileComponent } from '../../../../components/codalt-upload-file/codalt-upload-file.component';
import { PlanningFormData } from '../../../../services/entity-type.service';
import { FileStorage } from '../../../../services/file.service';

@Component({
    selector: 'app-trackline-planning-files',
    standalone: false,
    templateUrl: './trackline-planning-files.component.html',
    styleUrl: './trackline-planning-files.component.scss',
})
export class TracklinePlanningFilesComponent extends CodaltComponent implements OnInit {
    @Input() fieldFormData: PlanningFormData;
    @ViewChild(CodaltUploadFileComponent) uploadFileComponent: CodaltUploadFileComponent;

    user = LocalStorage.user;
    users: User[] = [];
    fileStorage = FileStorage.Planning;
    uploadControl = new FormControl<string>(null);
    allowedTypes = [
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif',
        'image/svg+xml',
        'application/pdf',
        'message/rfc822',
        '.msg',
        'application/vnd.ms-outlook',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'application/vnd.ms-excel',
        'text/csv',
        'text/plain',
    ];

    constructor(
        private planningService: PlanningService,
        private userService: UserService,
        private dialog: MatDialog
    ) {
        super();
    }

    ngOnInit() {
        this.fieldFormData.planningFiles = this.fieldFormData?.planningFiles?.length
            ? [...this.fieldFormData.planningFiles]
            : [...(this.fieldFormData?.orginal?.planning_files ?? [])];

        this.subscriptions.add(
            this.userService.getList(true).subscribe(users => {
                this.users = users;
            })
        );

        this.subscriptions.add(
            this.uploadControl.valueChanges.subscribe(uploadedFileName => {
                if (!uploadedFileName) {
                    return;
                }

                this.updateFile(uploadedFileName);

                this.uploadControl.setValue(null);
            })
        );
    }

    onError() {
        this.fieldFormData.planningFiles.pop();
    }

    addFile(file: File) {
        const planningFile: PlanningFile = {
            planning_id: this.fieldFormData?.orginal?.id,
            user_id: this.user?.id,
            name: file.name,
            file: null,
            created_at: new Date(),
        };

        this.fieldFormData.planningFiles = [...(this.fieldFormData?.planningFiles ?? []), planningFile];

        this.fieldFormData.form.markAsDirty();
    }

    updateFile(uploadedFileName: string) {
        this.fieldFormData.planningFiles.at(-1).file = uploadedFileName;
    }

    removeFile(fileToRemove: PlanningFile) {
        this.fieldFormData.planningFiles = (this.fieldFormData?.planningFiles ?? []).filter(
            file => file !== fileToRemove
        );

        this.fieldFormData.form.markAsDirty();
    }

    uploadDroppedFile(file: File) {
        if (this.uploadFileComponent.uploadFile(file)) {
            this.addFile(file);
        }
    }

    private getFile(planningFileId: number, cb: (data: Blob) => void) {
        this.subscriptions.add(
            this.planningService.getFile(planningFileId).subscribe(cb, error => {
                console.error('Error downloading file', error);
            })
        );
    }

    openFile(file: PlanningFile) {
        const fileName = file.name?.toLowerCase() ?? '';
        const isPdf = fileName.endsWith('.pdf');
        const isImage = /\.(jpe?g|png|gif|svg)$/.test(fileName);

        if (!isPdf && !isImage) {
            if (file.id) {
                this.downloadFile(file);
            }
            return;
        }

        if (!file.file) {
            return;
        }

        const url = `${environment.apiEndpoint}file/planning/${file.file}?access_token=${LocalStorage.getUserToken()}`;

        this.dialog.open(ImageModalComponent, {
            panelClass: 'image-viewer',
            width: '99vw',
            maxWidth: '1300px',
            height: '99vh',
            minHeight: '500px',
            maxHeight: '99vh',
            hasBackdrop: true,
            data: {
                viewIndex: 0,
                ...(isPdf ? { singlePdf: url } : { images: [url] }),
            },
        });
    }

    downloadFile(file: PlanningFile) {
        this.getFile(file.id, data => saveAs(data, file.name));
    }
}
